import { generateKey } from '#utils';

const isBrowser = typeof window !== 'undefined';

// Use session storage to save temporary data before the user logs in.
// Use partial login state from local storage to save data after the user logs in.
export const SESSION_STORAGE_KEYS = {
  IS_CREATE_ACCOUNT: 'isCreateAccount',
  AUTH_EMAIL: 'authEmail',
  AUTH_PASSWORD: 'authPassword',
  EMAIL_LOGIN: 'emailLogin',
  EMAIL_INPUT_STATE: 'emailInputState',
  INVITATION_TOKEN: 'invitationToken', // used to save invitation token from path /signup/{token}
  WORKSPACE: 'workspace', // used to save workspace from response of /invitation_decode
  INVITER: 'inviter', // used to save inviter from response of /invitation_decode
  FULL_NAME: 'fullName', // used to save name from response of /decode_registration_token
  SLACK_TOKEN: 'slackToken',
};

// Simple base64 encoding/decoding functions (same as localStorage)
const encode = (value: string | null): string => {
  if (value === null) return '';
  try {
    return btoa(value);
  } catch (e) {
    console.warn('Failed to encode value:', e);
    return value;
  }
};

const decode = (value: string | null): string | null => {
  if (!value) return null;
  try {
    return atob(value);
  } catch (e) {
    console.warn('Failed to decode value:', e);
    return value;
  }
};

export function getSessionItem<T>(key: string, defaultValue?: T) {
  if (!isBrowser) {
    return defaultValue !== undefined ? defaultValue : null;
  }

  try {
    const value = sessionStorage.getItem(generateKey(key));
    if (value) {
      // Decode the value before parsing
      const decodedValue = decode(value);
      if (decodedValue === null) {
        return defaultValue !== undefined ? defaultValue : null;
      }

      try {
        return JSON.parse(decodedValue) as T;
      } catch (_error) {
        // If JSON parsing fails, return the decoded value as-is
        return decodedValue as unknown as T;
      }
    }
    return defaultValue !== undefined ? defaultValue : null;
  } catch (error) {
    console.error(`Error getting sessionStorage key "${key}":`, error);
    return defaultValue !== undefined ? defaultValue : null;
  }
}

export function setSessionItem<T>(key: string, value: T): boolean {
  if (!isBrowser) {
    return false;
  }

  try {
    let storageValue: string;

    if (typeof value === 'object') {
      storageValue = value === null ? '' : JSON.stringify(value);
    } else {
      storageValue = value === undefined ? 'null' : String(value);
    }

    // Encode the value before storing
    const encodedValue = encode(storageValue);
    sessionStorage.setItem(generateKey(key), encodedValue);
    return true;
  } catch (error) {
    console.error(`Error setting sessionStorage key "${key}":`, error);
    return false;
  }
}

export function removeSessionItem(key: string): boolean {
  if (!isBrowser) {
    return false;
  }

  try {
    sessionStorage.removeItem(generateKey(key));
    return true;
  } catch (error) {
    console.error(`Error removing sessionStorage key "${key}":`, error);
    return false;
  }
}

export function hasSessionItem(key: string): boolean {
  if (!isBrowser) {
    return false;
  }

  try {
    return sessionStorage.getItem(generateKey(key)) !== null;
  } catch (error) {
    console.error(`Error checking sessionStorage key "${key}":`, error);
    return false;
  }
}

export function clearSessionStorage(): boolean {
  if (!isBrowser) {
    return false;
  }

  try {
    sessionStorage.clear();
    return true;
  } catch (error) {
    console.error('Error clearing sessionStorage:', error);
    return false;
  }
}
